/**
 * Whether the "Already receiving?" question applies to a person's SSI.
 *
 * A selected begin age settles it: SSI can only have started already if that
 * age is at or before the person's age *today*, not their plan-year age.
 * Someone born 12/31/1962 is 63 for all of 2026, so a begin age of 64 is still
 * in the future even though `person.age` already reads 64.
 *
 * The fallback covers the one case with no age to compare: once SSI is saved as
 * already receiving, the saved begin age is cleared. Keep asking there, or the
 * only control that can turn "already receiving" back off disappears and the
 * choice becomes permanent.
 *
 * @param {number|string|null} beginAge selected SSI begin age
 * @param {boolean} initialReceivingSsi whether the saved record says SSI has started
 * @param {number|null} ageToday the person's age as of today
 * @returns {boolean}
 */
export function showReceivingSsiQuestion({ beginAge, initialReceivingSsi, ageToday }) {
  const selectedAge = parseInt(beginAge)
  if (Number.isNaN(selectedAge)) {
    return initialReceivingSsi === true
  }
  return selectedAge <= ageToday
}

/**
 * The "already receiving" value to start the form with.
 *
 * A saved record can pair "already receiving" with a begin age still in the
 * future — the state the old age check allowed through. The begin age is what
 * the form puts on screen, so treat it as the source of truth and drop the
 * stale flag. The question then stays hidden (correct for a future age) and
 * saving writes the record back consistent, instead of stranding the person
 * with a flag they have no control to clear.
 *
 * @param {number|string|null} beginAge saved SSI begin age
 * @param {number|null} receivingSsi saved receiving flag (1 = already receiving)
 * @param {number|null} ageToday the person's age as of today
 * @returns {boolean}
 */
export function initialReceivingSsi({ beginAge, receivingSsi, ageToday }) {
  if (!receivingSsi) {
    return false
  }
  const selectedAge = parseInt(beginAge)
  return Number.isNaN(selectedAge) || selectedAge <= ageToday
}
